/**
 * useCloudGoals — Manages cloud-synced goals for authenticated users via Supabase.
 */
import { Goal } from "@/types/todo";
import { useGoalQueries } from "./useGoalQueries";
import { useTodoMutations } from "./todo-mutations"; 
import { useSubtaskMutations } from "./useSubtaskMutations";
import { useSubSubtaskMutations } from "./useSubSubtaskMutations";
import { useTodoOperations } from "./goal-operations/useTodoOperations";
import { useSubtaskOperations } from "./goal-operations/useSubtaskOperations";
import { useSubSubtaskOperations } from "./goal-operations/useSubSubtaskOperations";
import { useGoalOperations } from "./goal-operations/useGoalOperations";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useTodoDateHandler } from "./goal-handlers/useTodoDateHandler";
import { logger } from "@/utils/logger";

export function useCloudGoals(userId?: string) {
  const { user } = useAuth();
  const { goals, isLoading, error, invalidateGoals } = useGoalQueries();
  
  const { addTodo, toggleTodo, deleteTodo, updateTodoDueDate, updateTodoText } = useTodoMutations();
  const { addSubtask, toggleSubtask, deleteSubtask, updateSubtaskText } = useSubtaskMutations();
  const { addSubSubtask, toggleSubSubtask, deleteSubSubtask } = useSubSubtaskMutations();
  
  const todoOperations = useTodoOperations(
    addTodo,
    toggleTodo,
    invalidateGoals,
    supabase
  );
  
  const subtaskOperations = useSubtaskOperations(
    invalidateGoals
  );
  
  const subSubtaskOperations = useSubSubtaskOperations(
    invalidateGoals
  );
  
  const goalOperations = useGoalOperations(
    invalidateGoals,
    userId
  );
  
  const dateHandler = useTodoDateHandler(goals || []);
  
  const handleUpdateTodoDueDate = (params: { goalTitle: string, todoId: string, newDate: Date | string | null }): Promise<any> => {
    const requestId = `cloud-${Date.now()}`;
    logger.debug(`[CloudGoals] [${requestId}] Handling updateTodoDueDate:`, params);
    
    try {
      if (dateHandler?.handleUpdateDueDate) {
        logger.debug(`[CloudGoals] [${requestId}] Using dateHandler.handleUpdateDueDate`);
        return dateHandler.handleUpdateDueDate(params.goalTitle, params.todoId, params.newDate);
      }
      
      return new Promise(async (resolve, reject) => {
        try {
          const result = await updateTodoDueDate(params);
          logger.debug(`[CloudGoals] [${requestId}] Update successful:`, result);
          
          logger.debug(`[CloudGoals] [${requestId}] Forcing invalidation of goal queries`);
          await invalidateGoals();
          
          resolve(result);
        } catch (error) {
          logger.error(`[CloudGoals] [${requestId}] Update failed:`, error);
          reject(error);
        }
      });
    } catch (error) {
      logger.error(`[CloudGoals] [${requestId}] Error in updateTodoDueDate:`, error);
      return Promise.reject(error);
    }
  };
  
  return {
    goals,
    isLoading,
    error,
    addGoal: goalOperations.addGoal,
    deleteGoal: goalOperations.deleteGoal,
    updateGoal: goalOperations.updateGoal,
    addTodo: todoOperations.addTodo,
    deleteTodo: todoOperations.deleteTodo,
    toggleTodo: todoOperations.toggleTodo,
    updateTodo: (params: { goalTitle: string; todoId: string; newText: string }) => {
      logger.debug('[CloudGoals] Calling updateTodoText mutation');
      updateTodoText({ todoId: params.todoId, newText: params.newText });
      return Promise.resolve();
    },
    updateTodoDueDate: handleUpdateTodoDueDate,
    addSubtask: subtaskOperations.addSubtask,
    deleteSubtask: subtaskOperations.deleteSubtask,
    toggleSubtask: subtaskOperations.toggleSubtask,
    updateSubtask: subtaskOperations.updateSubtask,
    addSubSubtask: subSubtaskOperations.addSubSubtask,
    deleteSubSubtask: subSubtaskOperations.deleteSubSubtask,
    toggleSubSubtask: subSubtaskOperations.toggleSubSubtask
  };
}
